import { CustomOutputLayout } from './CustomOutputLayout';
import { Instructions } from './Instructions';
import { StandardOutputLayout } from './StandardOutputLayout';
import { OutputLayoutInputProps } from '../common';
import {
  checkDuplicates,
  extractInputFieldsFromProgram,
  getDiff,
} from '../common/output-layout-utils';
import {
  loadLatestFields,
  prepareStandardOutputFields,
} from '../common/output-layout-service';
import { modalUtils } from '@/app/core/utils';
import {
  useDataPart,
  useDataStore,
  useStepContext,
} from '@/app/core/components/step-wizard';
import { useProgramDependentDataContext } from '@/app/core/components/program-wizard/program-dependent-data-context';
import { Box, Button, Flex, Icon, Text } from '@chakra-ui/react';
import { yupResolver } from '@hookform/resolvers';
import {
  FieldDelimiter,
  OutputLayoutInformation,
  outputLayoutInformationValidator,
  SSARProgram,
} from '@ssar-app/domain';
import {
  Card,
  Modal,
  ModalFunctionProps,
  RadioGroup,
  Spin,
} from '@ssar-app/uikit';
import { cloneDeep } from 'lodash';
import { ReactNode, useCallback, useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import {
  AiOutlineCloseCircle,
  AiOutlineInfoCircle,
} from 'react-icons/ai/index.js';
import axios, { CancelTokenSource } from 'axios';
import { ErrorOption } from 'react-hook-form/dist/types/form';

export type OutputLayoutMode = 'standard' | 'custom';

export const OutputLayout = (props: OutputLayoutInputProps) => {
  const { stepKey } = props;
  const { actionsHandler } = props.moduleProps;
  const { isStepActive: isActive } = useStepContext();
  const { data } = useDataStore();
  const [dataPartState, setDataPartState] =
    useDataPart<OutputLayoutInformation>(stepKey);

  const [model, setModel] = useState<OutputLayoutInformation>({
    fields: dataPartState?.data?.fields || [],
    delimiter: dataPartState?.data?.delimiter || FieldDelimiter.COMMA,
    isCustomized: dataPartState?.data?.isCustomized || false,
  });
  const [isLoading, setIsLoading] = useState(false);
  const [showInstructions, setShowInstructions] = useState(false);
  const { metaData, setMetaData } = useProgramDependentDataContext();

  const [cancelTokenSource, setCancelTokenSource] =
    useState<CancelTokenSource>();
  const formMethods = useForm<OutputLayoutInformation>({
    defaultValues: {
      fields: dataPartState?.data?.fields || [],
      delimiter: dataPartState?.data?.delimiter || FieldDelimiter.COMMA,
    },
    mode: 'onChange',
    resolver: yupResolver(outputLayoutInformationValidator),
  });
  const { setError, errors, clearErrors, setValue } = formMethods;

  const updateData = useCallback(() => {
    setDataPartState((currentState) => {
      const newState = {
        ...currentState,
        isValid: !errors.fields,
        data: {
          ...currentState.data,
          ...model,
        },
      };
      return newState;
    });
  }, [setDataPartState, errors.fields, model]);

  const setPrevWizardData = (prevData: SSARProgram) => {
    setMetaData({ ...metaData, prevData: prevData });
  };

  const resetToStandardOutputFields = async (isCustomized = false) => {
    if (!isLoading) {
      setIsLoading(true);
    }
    // eslint-disable-next-line import/no-named-as-default-member
    const cancelToken = axios.CancelToken.source();
    cancelTokenSource?.token && cancelTokenSource.cancel();
    setCancelTokenSource(cancelToken);

    prepareStandardOutputFields(
      data as SSARProgram,
      isCustomized,
      setPrevWizardData,
    )
      .then((resetProgram) => {
        setModel({
          ...model,
          isCustomized: isCustomized,
          fields: resetProgram.outputLayout.fields,
        });
        actionsHandler.reset(resetProgram.outputLayout.fields);
      })
      .catch((error) => {
        if (!error?.__CANCEL__) {
          modalUtils.showError(error, 'Error loading Output Layout');
        }
      })
      .finally(() => {
        setIsLoading(false);
      });
  };

  const findDiffAndPrepareOutputFields = () => {
    if (isLoading) {
      return;
    }
    const presentData = cloneDeep(data || {}) as SSARProgram;
    const differences = getDiff(metaData?.prevData as SSARProgram, presentData);

    if (differences.hasChanged) {
      if (model.isCustomized) {
        if (
          extractInputFieldsFromProgram(differences.added).length !== 0 ||
          extractInputFieldsFromProgram(differences.removed).length !== 0
        ) {
          showWarningPopup({
            header: 'Rebuilding the layout as Input file is changed...',
            showCancel: false,
            showCloseButton: false,
            closeOnOverlayClick: false,
            onOk: () => {
              setModel({ ...model, isCustomized: false });
              setIsLoading(true);
              resetToStandardOutputFields();
            },
          });
          return;
        }
        showWarningPopup({
          header:
            'Addition/Removal of fields are available based on previous steps data.',
          showCancel: false,
          showCloseButton: false,
          closeOnOverlayClick: false,
          onOk: () => {
            setIsLoading(true);
            loadLatestFields(
              differences.added as SSARProgram,
              differences.removed as SSARProgram,
              presentData,
              setPrevWizardData,
            )
              .then((outputProgram) => {
                setModel({
                  ...model,
                  fields: outputProgram.outputLayout.fields,
                });
                actionsHandler.reset(outputProgram.outputLayout.fields);
              })
              .catch((_error) => {
                Modal.error({
                  ...getCommonProps(
                    'Failed to load Output Fields.',
                    <Icon
                      as={AiOutlineCloseCircle}
                      color='orange.500'
                      h={20}
                      w={20}
                    />,
                  ),
                  okText: 'Try Again',
                  onClose: () =>
                    loadLatestFields(
                      differences.added as SSARProgram,
                      differences.removed as SSARProgram,
                      presentData,
                      setPrevWizardData,
                    ),
                });
              })
              .finally(() => setIsLoading(false));
          },
        });
      } else {
        setIsLoading(true);
        resetToStandardOutputFields();
      }
    }
  };

  const getCommonProps = (
    header: ReactNode,
    icon: ReactNode,
  ): ModalFunctionProps => ({
    header: (
      <Text color='gray.900' fontWeight='normal'>
        {header}
      </Text>
    ),
    headerProps: {
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      gap: 4,
      paddingTop: 12,
      paddingBottom: 6,
    },
    icon: icon,
    containerProps: { textAlign: 'center' },
    okButtonProps: { size: 'sm' },
    cancelButtonProps: { size: 'sm' },
  });

  const showWarningPopup = (props: ModalFunctionProps) => {
    const {
      header = (
        <>
          The fields that you customised will be lost. <br />
          Are you sure you want to continue?
        </>
      ),
    } = props;
    Modal.warning({
      ...getCommonProps(
        header,
        <Icon as={AiOutlineInfoCircle} color='orange.500' h={20} w={20} />,
      ),
      ...props,
    });
  };

  useEffect(() => {
    if (isActive) {
      findDiffAndPrepareOutputFields();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isActive]);

  useEffect(() => {
    setValue('fields', model.fields);
    const { containsDups } = checkDuplicates(model.fields || []);
    !containsDups
      ? clearErrors('fields')
      : setError('fields', {} as ErrorOption);

    updateData();

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [model]);

  useEffect(() => {
    setModel({ ...model, ...(metaData?.outputLayout || {}) });
    actionsHandler.reset(metaData?.outputLayout?.fields || []);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [metaData?.outputLayout]);

  useEffect(() => {
    const tmpProgram = data as SSARProgram;
    actionsHandler.reset(tmpProgram.outputLayout.fields);
    setPrevWizardData(cloneDeep(tmpProgram));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (!isActive) {
    return null;
  }

  return (
    <>
      <Card cardProps={{ borderColor: 'gray.300', gap: 4, mb: 2.5 }}>
        <Modal
          isOpen={showInstructions}
          onClose={() => setShowInstructions(false)}
          header='Instructions'
          showOk={false}
          showCancel
          cancelText='Close'
          size='3xl'
          cancelButtonProps={{ size: 'sm' }}
        >
          <Instructions />
        </Modal>
        <Spin
          spinning={isLoading}
          spinWrapperProps={{ zIndex: 9999 }}
          tip='Loading Output fields...'
        >
          <Flex py={2} borderBottom='1px solid' borderColor='gray.300'>
            <RadioGroup
              onChange={(val: OutputLayoutMode) => {
                if (val === 'standard') {
                  showWarningPopup({
                    onOk: () => {
                      setIsLoading(true);
                      resetToStandardOutputFields();
                    },
                    showCancel: true,
                  });
                } else {
                  setModel({ ...model, isCustomized: true });
                }
              }}
              optionsType='button'
              value={model.isCustomized ? 'custom' : 'standard'}
              options={[
                { label: 'Standard Output', value: 'standard' },
                { label: 'Custom Output', value: 'custom' },
              ]}
              applyBorderRadiusForStartEndButtons={true}
              borderRadiusForStartEndButtons={5}
            />
            {model.isCustomized && (
              <Button
                ml='auto'
                leftIcon={<Icon as={AiOutlineInfoCircle} />}
                border='1px solid'
                borderColor='gray.300'
                color='primary'
                size='sm'
                onClick={() => setShowInstructions(true)}
              >
                Read the instructions before customizing the layout
              </Button>
            )}
          </Flex>
          {!isLoading && (
            <>
              {!model.isCustomized ? (
                <StandardOutputLayout {...props} outputFields={model.fields} />
              ) : (
                <CustomOutputLayout
                  {...props}
                  actionsHandler={actionsHandler}
                  isActive={isActive}
                  outputFields={model.fields}
                  refreshLayout={resetToStandardOutputFields}
                  showWarningPopup={showWarningPopup}
                  clearErrors={clearErrors}
                  setError={setError}
                  setFields={(newFields) => {
                    setModel({ ...model, fields: newFields });
                  }}
                />
              )}
            </>
          )}
          {isLoading && <Box h={'48'}></Box>}
        </Spin>
      </Card>
    </>
  );
};
